import React, { useState, useRef, useEffect } from 'react';
import Typeahead from '../../molecules/Typeahead';
import LoqateAddressLookupProps, { Option } from './LoqateAddressLookup.types';
import Button from '../../atoms/Button';
import AddressOption from './AddressOption';
import styles from './LoqateAddressLookup.module.scss';

const LoqateAddressLookup = ({
  serviceCaller,
  findAddressURL,
  retrieveAddressURL,
  minSearchLength = 1,
  dataPointer = '/',
  childDataSchema,
  setValue,
  toggleDisplay,
  hideAll,
  onSelect,
  dataSchemaToLoqateFieldMap,
  showManualInputText,
  showSearchInputText,
  startText,
  noMatchText,
  noMatchManualFormLinkText,
  loadingText,
  showManualForm = true,
  debounceDelay = 300,
  errorMessage,
  countries,
  dataCy,
  placeholder,
  invalid,
  ariaLabel,
  errorMsgHolderId,
}: LoqateAddressLookupProps) => {
  const [showManualInput, setShowManualInput] = useState<boolean>(false);
  const currentSearchString = useRef<string>('');

  // throw away readonly properties
  const addressChildProperties =
    childDataSchema &&
    Object.keys(childDataSchema).reduce((result, key) => {
      if (!childDataSchema[key]?.readOnly) result.push(key);
      return result;
    }, []);

  const createOptions = (loqateAddressList): Option[] => {
    if (loqateAddressList) {
      const newOptions = loqateAddressList.items.map((loqateItem) => {
        const isGroup = loqateItem.type !== 'Address';
        return {
          title: <AddressOption text={loqateItem.text} description={loqateItem.description} isGroup={isGroup} />,
          params: {
            textDescription: loqateItem.text || loqateItem.description,
            type: loqateItem.type,
            loqateId: loqateItem.id,
            continueFiltering: isGroup,
          },
          const: loqateItem,
        };
      });

      return newOptions;
    }
    return null;
  };

  const setValueAndToggleDisplay = (inputsDataPointer: string, inputsValue: string) => {
    setValue(inputsDataPointer, inputsValue);
    toggleDisplay(inputsDataPointer);
  };

  const displayManualInputsWithValues = (loqateAddressDetails = { items: [] }) => {
    const details = loqateAddressDetails.items[0];
    addressChildProperties.forEach((key) => {
      const childDataPointer: string = `${dataPointer}/${key}`;
      const loqateKey: string = dataSchemaToLoqateFieldMap[key];
      const value: string = details?.[loqateKey] || '';
      setValueAndToggleDisplay(childDataPointer, value);
      setShowManualInput(true);
    });
  };

  const handleHide = (inputsDataPointer: string) => {
    if (hideAll) {
      hideAll(inputsDataPointer);
    }
  };

  useEffect(() => {
    if (addressChildProperties) {
      addressChildProperties.forEach((key) => {
        const childDataPointer: string = `${dataPointer}/${key}`;
        handleHide(childDataPointer);
      });
    }
  }, []);

  const handleFormTypeClick = () => {
    addressChildProperties.forEach((key) => {
      const childDataPointer: string = `${dataPointer}/${key}`;
      setValueAndToggleDisplay(childDataPointer, '');
      setShowManualInput(!showManualInput);
    });
  };

  // the API is current using the parameter postcode as Text
  // when the API changes this will need updating
  const getData = async () => {
    const response = serviceCaller({
      url: findAddressURL,
      method: 'get',
      query: {
        postcode: currentSearchString.current,
        countries,
      },
    });
    const { data } = await response;
    return data;
  };

  const getDataWithinContainer = async (loqateId: string) => {
    const response = serviceCaller({
      url: findAddressURL,
      method: 'get',
      query: {
        container: loqateId,
      },
    });
    const { data } = await response;
    return data;
  };

  const getDetails = async (loqateId: string) => {
    const response = serviceCaller({
      url: retrieveAddressURL,
      method: 'get',
      query: {
        id: loqateId,
      },
    });
    const { data } = await response;
    if (addressChildProperties) {
      displayManualInputsWithValues(data);
    } else {
      onSelect(data);
    }
  };

  const filterFunction = async (search, currentOptions, selectedOption): Promise<Option[]> => {
    currentSearchString.current = search;
    if (selectedOption && !selectedOption.params.continueFiltering) {
      return [];
    }

    let loqateResponse;
    if (selectedOption && selectedOption.params.loqateId) {
      loqateResponse = await getDataWithinContainer(selectedOption.params.loqateId);
    } else {
      loqateResponse = await getData();
    }

    return createOptions(loqateResponse);
  };

  const handleSelection = (selectedOption) => {
    const { id } = selectedOption.value;
    getDetails(id);
  };

  return (
    <>
      {!showManualInput ? (
        <div>
          <Typeahead
            options={[]}
            onChange={handleSelection}
            filterFunction={filterFunction}
            startText={startText}
            noMatchText={
              <>
                {noMatchText}
                {noMatchManualFormLinkText && (
                  <Button variant='link' onClick={handleFormTypeClick}>
                    {noMatchManualFormLinkText}
                  </Button>
                )}
              </>
            }
            loadingText={loadingText}
            minSearchLength={minSearchLength}
            dataPointer={dataPointer}
            debounceDelay={debounceDelay}
            errorMessage={errorMessage}
            placeholder={placeholder}
            errorMsgHolderId={errorMsgHolderId}
            dataCy={dataCy}
            invalid={invalid}
            ariaLabel={ariaLabel}
          />
          {showManualForm && showManualInputText && (
            <Button variant='link' onClick={handleFormTypeClick}>
              {showManualInputText}
            </Button>
          )}
        </div>
      ) : (
        <Button variant='link' onClick={handleFormTypeClick} className={styles.showSearchAddress}>
          {showSearchInputText}
        </Button>
      )}
    </>
  );
};

export default LoqateAddressLookup;
